import type { User } from '@stores/User';
import { userStore } from '@stores/User';
import { reaction, runInAction } from 'mobx';

/**
 * User Settings Service
 * Provides a localStorage-like interface for managing user settings
 * Settings are stored on the server and cached locally for performance
 */
export class UserSettingsService {
  private static instance: UserSettingsService;
  private cache: Record<string, unknown> = {};
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private crossStoreReactionsBound = false;
  // Tracks whether one of our own saveSettingsToServer() calls is mid-flight.
  // The reaction below watches `userStore.data.settings` to catch EXTERNAL
  // mutations (e.g. userStore.fetch), but that same observable also changes
  // from inside saveSettingsToServer itself. Without this guard, concurrent
  // set() calls can race: POST #1 response updates the cache while POST #2's
  // cache state is still pending, and the reaction re-applies the stale
  // POST #1 response on top.
  private isSaving = false;

  private constructor() {
    // No store reads here: this singleton is created at module scope inside
    // an import cycle with User.ts (via the router) — see
    // initCrossStoreReactions() below.
  }

  /**
   * Keep the cache aligned with userStore.data.settings whenever it changes
   * outside of our own set() path — e.g. when userStore.fetch() replaces the
   * user object after a GET /user/me. Without this, writes through this
   * service would be working off a stale cache.
   *
   * Bound from src/index.tsx once the whole module graph has evaluated:
   * reading `userStore` during construction throws a TDZ ReferenceError when
   * this module evaluates while User.ts is still initializing, and even a
   * queueMicrotask deferral raced module init in production chunk orders.
   * Nothing can write settings before first paint, so late binding is safe.
   */
  initCrossStoreReactions() {
    if (this.crossStoreReactionsBound) return;
    this.crossStoreReactionsBound = true;
    reaction(
      () => userStore.data?.settings,
      (settings) => {
        if (!settings) return;
        if (this.isSaving) return;
        this.cache = { ...settings };
      },
    );
  }

  static getInstance(): UserSettingsService {
    if (!UserSettingsService.instance) {
      UserSettingsService.instance = new UserSettingsService();
    }
    return UserSettingsService.instance;
  }

  /**
   * Initialize the settings service by loading current settings from the server
   */
  private async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.loadSettingsFromServer();
    try {
      await this.initializationPromise;
      this.isInitialized = true;
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Load settings from the server
   */
  private async loadSettingsFromServer(): Promise<void> {
    try {
      // Get current user data which includes settings
      const user = await userStore.fetch();
      if (user?.settings) {
        this.cache = { ...user.settings };
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
      // Initialize with empty settings if loading fails
      this.cache = {};
    }
  }

  /**
   * Save settings to the server
   */
  private async saveSettingsToServer(): Promise<void> {
    this.isSaving = true;
    try {
      // The response contains the updated user object
      const updatedUser = await userStore.apiWithAuth<User>('/user/me/settings', {
        method: 'POST',
        body: this.cache,
      });

      // Update the cache with the new settings from the response
      if (updatedUser?.settings) {
        this.cache = { ...updatedUser.settings };
      }

      // Mirror the server's settings back into the user store.
      //
      // Assign the nested property IN PLACE rather than replacing `userStore.data`.
      // `DataFrame.data` is deeply observable, so this still notifies the reaction above
      // (`() => userStore.data?.settings`) — but replacing the object changes the identity
      // `TrackingStore` watches (`reaction(() => userStore.data, …)`), which re-fires
      // `mixpanel.identify` + `people.set` on every settings write and re-renders every
      // observer reading `userStore.data` app-wide. Pinning a library goes through here,
      // so that was once per pin. `UIPreferences.ts` avoids the same trap for the same
      // reason — see the note above its own `runInAction`.
      runInAction(() => {
        if (updatedUser?.settings && userStore.data) {
          userStore.data.settings = updatedUser.settings;
        }
      });
    } catch (error) {
      console.error('Failed to save user settings:', error);
      throw new Error('Failed to save settings to server');
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Get a setting value by key
   * @param key - The setting key
   * @param defaultValue - Default value if the key doesn't exist
   * @returns The setting value or defaultValue
   */
  async get<T = unknown>(key: string, defaultValue?: T): Promise<T | undefined> {
    await this.initialize();
    return (this.cache[key] as T) ?? defaultValue;
  }

  /**
   * Set a setting value
   * @param key - The setting key
   * @param value - The value to set
   */
  async set<T>(key: string, value: T): Promise<void> {
    await this.initialize();
    this.cache[key] = value;
    await this.saveSettingsToServer();
  }

  /**
   * Remove a setting
   * @param key - The setting key to remove
   */
  async remove(key: string): Promise<void> {
    await this.initialize();
    if (key in this.cache) {
      delete this.cache[key];
      await this.saveSettingsToServer();
    }
  }

  /**
   * Check if a setting key exists
   * @param key - The setting key to check
   * @returns True if the key exists
   */
  async has(key: string): Promise<boolean> {
    await this.initialize();
    return key in this.cache;
  }

  /**
   * Get all settings as an object
   * @returns All current settings
   */
  async getAll(): Promise<Record<string, unknown>> {
    await this.initialize();
    return { ...this.cache };
  }

  /**
   * Clear all settings
   */
  async clear(): Promise<void> {
    await this.initialize();
    this.cache = {};
    await this.saveSettingsToServer();
  }

  /**
   * Get the number of settings
   * @returns The number of settings
   */
  async size(): Promise<number> {
    await this.initialize();
    return Object.keys(this.cache).length;
  }

  /**
   * Get all setting keys
   * @returns Array of setting keys
   */
  async keys(): Promise<string[]> {
    await this.initialize();
    return Object.keys(this.cache);
  }

  /**
   * Refresh settings from the server (useful after external changes)
   */
  async refresh(): Promise<void> {
    this.isInitialized = false;
    await this.initialize();
  }
}

// Export a singleton instance
export const userSettings = UserSettingsService.getInstance();

// Convenience functions for common use cases
export const getUserSetting = <T = unknown>(
  key: string,
  defaultValue?: T,
): Promise<T | undefined> => {
  return userSettings.get(key, defaultValue);
};

export const setUserSetting = <T>(key: string, value: T): Promise<void> => {
  return userSettings.set(key, value);
};

export const removeUserSetting = (key: string): Promise<void> => {
  return userSettings.remove(key);
};

export const hasUserSetting = (key: string): Promise<boolean> => {
  return userSettings.has(key);
};
